import { Box, Typography } from "@mui/material";
import React from "react";
import AddLegalEntityDialog from "../../../legalEntity/addLegalEntityDialog/AddLegalEntityDialog";
import { useTranslation } from "@localization/useTranslation.ts";
import { useUrlProvider } from "../../../useUrlProvider";
import { SelectableLegalUnitsItem } from "../../ProcessingActivity.types";
import { useSharingsLegalEntityHook } from "./sharingsLegalEntity.hooks";
import { useSharingsLegalEntityStyles } from "./useSharingsLegalEntityStyles";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";
import DataStorageLocationDialog from "../dataProcessorsStep/dataStorageLocationDialog/DataStorageLocationDialog";
import { DotLegalSelectOption } from "@common/components/dotLegalMultiSelect/DotLegalMultiSelect.types.ts";
import DotLegalSelectWithButton from "../../../common/components/dotLegalSelectWithButton/DotLegalSelectWithButton";
import { useRiskIndicator } from "../../../riskAssessment/riskIndicator/RiskIndicator.hooks";
import { classificationIcon } from "@common/icons.tsx";
import { useLocation, useNavigate } from "react-router";
import LegalEntityClassificationDialog from "../../../legalEntity/legalEntityClassificationTab/legalEntityClassificationDialog/LegalEntityClassificationDialog";
import { CreateLegalEntityReturnType, LegalEntityScope } from "../../../legalEntity/legalEntities/LegalEntities.types";
import { ClassificationInfo } from "../dataProcessorsStep/DataProcessorStep.types";

export interface ISharingsLegalEntity {
    options: Array<SelectableLegalUnitsItem> | undefined;
    selectedItem?: string | null;
    disabled?: boolean;
    placeholder: string;
    label: string;
    className?: string;
    isLoading?: boolean;
    toolTipText?: string;
    helperText?: string;
    errorText?: string;
    onLegalEntityChange: (legalEntity: string | null) => void;
    showAddNewEntityButton: boolean;
    showStorageLocationLink?: boolean;
    isThirdCountry?: boolean;
    onDataStorageLocationSave?: (storageLocations: Array<string>, supportLocations: Array<string>) => void;
    countriesLoading?: boolean;
    countryList?: Array<DotLegalSelectOption>;
    selectedStorageLocations?: Array<string>;
    selectedSupportLocations?: Array<string>;
    warningText?: string;
    classificationInfo?: ClassificationInfo;
    processingActivityId?: string;
    createAndNavigateName?: string;
    createAndNavigateReturnType?: CreateLegalEntityReturnType;
}

export default function SharingsLegalEntity(props: ISharingsLegalEntity) {
    const styles = useSharingsLegalEntityStyles(props);
    const { translateString } = useTranslation();
    const { permissions } = useUserContext();
    const {
        showAddLegalEntityDialog,
        setShowAddLegalEntityDialog,
        addLegalEntity,
        toggleCompanyLink,
        setShowStorageLocationDialog,
        showStorageLocationDialog,
        setShowClassificationDialog,
        showClassificationDialog,
        createAndNavigate,
    } = useSharingsLegalEntityHook(props);
    const { getLegalEntityUrl, getGroupEntityUrl, getLegalEntityClassificationUrl, getClassificationQuestionnaireResponseReport } = useUrlProvider();
    const { getRiskIcon } = useRiskIndicator();
    const navigate = useNavigate();
    const location = useLocation();

    const canAddNewEntity = props.showAddNewEntityButton && permissions.canCreateLegalEntitiesInline;

    function getLink() {
        if (toggleCompanyLink.showGroupCompanyLink) {
            return getGroupEntityUrl(toggleCompanyLink.companyId);
        } else if (toggleCompanyLink.showCompanyLink) {
            return getLegalEntityUrl(toggleCompanyLink.companyId);
        }
    }

    const onClickCreateNewCompany = () => {
        if (!props.disabled) {
            setShowAddLegalEntityDialog(true);
        }
    };

    function getClassificationTooltipText(): string {
        let classificationInfo = props.classificationInfo;

        if (!classificationInfo?.hasClassification) {
            return translateString("addClassificationToDataProcessor");
        }

        if (classificationInfo.isSubmitted) {
            return `${classificationInfo.questionnaireName}: ${classificationInfo.scoreEnabled ? classificationInfo.riskName : translateString("none")}`;
        }

        return `${translateString("answerClassification")} "${classificationInfo.questionnaireName}"`;
    }

    function getClassificationUrl() {
        if (props.classificationInfo?.isSubmitted) {
            return getClassificationQuestionnaireResponseReport(
                props.classificationInfo!.responseId!,
                props.options!.find((x) => x.id === props.selectedItem)!.name,
                false,
                false
            );
        }

        return getLegalEntityClassificationUrl(
            LegalEntityScope.External,
            props.selectedItem!,
            props.classificationInfo!.responseId!,
            location.pathname + location.search + location.hash
        );
    }

    function getClassificationAdditionalIcon(): { tooltipText: string; icon: JSX.Element } | undefined {
        if (props.disabled || !props.selectedItem || !props.classificationInfo) {
            return undefined;
        }

        const icon = props.classificationInfo.isSubmitted ? getRiskIcon(props.classificationInfo.riskLevel) : classificationIcon;
        const tooltipText = getClassificationTooltipText();

        return {
            tooltipText,
            icon: (
                <Box
                    sx={{
                        cursor: "pointer",
                        "& svg": {
                            width: 18,
                        },
                        display: "flex",
                        alignItems: "center",
                        height: "75%",
                    }}
                    onClick={() => {
                        if (!props.classificationInfo?.hasClassification) {
                            setShowClassificationDialog(true);
                        } else {
                            navigate(getClassificationUrl());
                        }
                    }}
                >
                    {icon}
                </Box>
            ),
        };
    }

    let legalEntitySelector = (
        <DotLegalSelectWithButton
            selectedItem={props.selectedItem}
            isLoading={props.isLoading}
            disabled={props.disabled}
            errorText={props.errorText}
            label={props.label}
            placeholder={props.placeholder}
            onChange={props.onLegalEntityChange}
            options={props.options}
            toolTipText={props.toolTipText}
            warningText={props.isThirdCountry ? (props.warningText ? props.warningText : translateString("insecureThirdCountry")) : ""}
            link={toggleCompanyLink.showLink() ? { tooltip: translateString("goToLegalEntity"), href: getLink()! } : undefined}
            noOptionsLabel={translateString("noOptions")}
            buttonLabel={translateString("createLegalEntity")}
            onButtonClick={onClickCreateNewCompany}
            hasPermission={canAddNewEntity}
            additionalIcon={getClassificationAdditionalIcon()}
            noMargin={canAddNewEntity || props.showStorageLocationLink}
        />
    );
    return (
        <>
            {canAddNewEntity || props.showStorageLocationLink ? (
                <Box sx={styles.container}>
                    <Box sx={{ marginTop: 2, marginBottom: 1 }}>
                        {legalEntitySelector}
                        <Box sx={styles.linkLabel}>
                            {props.showStorageLocationLink && (
                                <Typography onClick={() => setShowStorageLocationDialog(true)} sx={styles.descriptionLabel}>
                                    {props.disabled ? translateString("seeStorageLocation") : translateString("specifyStorageLocation")}
                                </Typography>
                            )}

                            {canAddNewEntity && (
                                <Typography onClick={onClickCreateNewCompany} sx={styles.descriptionLabel}>
                                    {translateString("createLegalEntity")}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                    {showStorageLocationDialog && (
                        <DataStorageLocationDialog
                            countriesLoading={props.countriesLoading ?? false}
                            countryList={props.countryList!}
                            storageLocations={props.selectedStorageLocations!}
                            supportLocations={props.selectedSupportLocations!}
                            readOnly={props.disabled ?? false}
                            onCloseDialog={() => setShowStorageLocationDialog(false)}
                            onSave={(storageLocations: Array<string>, supportLocations: Array<string>) => {
                                setShowStorageLocationDialog(false);
                                props.onDataStorageLocationSave!(storageLocations, supportLocations);
                            }}
                        ></DataStorageLocationDialog>
                    )}
                    {showAddLegalEntityDialog && !props.isLoading && (
                        <AddLegalEntityDialog
                            onDialogClose={() => setShowAddLegalEntityDialog(false)}
                            onEntityCreated={(id) => addLegalEntity(id)}
                            onCreatedAndRedirect={createAndNavigate}
                        />
                    )}
                </Box>
            ) : (
                <>{legalEntitySelector}</>
            )}

            {showClassificationDialog && (
                <LegalEntityClassificationDialog
                    legalEntityId={props.selectedItem!}
                    onDialogClose={() => setShowClassificationDialog(false)}
                    onCreated={(responseId?: string) => {
                        setShowClassificationDialog(false);
                        navigate(
                            getLegalEntityClassificationUrl(
                                LegalEntityScope.External,
                                props.selectedItem!,
                                responseId!,
                                location.pathname + location.search + location.hash
                            )
                        );
                    }}
                    planLimit={undefined}
                    processingActivityId={props.processingActivityId}
                    btnText={translateString("answerClassification")}
                    infoText={translateString("addClassificationFromSharingInfo")}
                />
            )}
        </>
    );
}
